import {
  Block,
  Button,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Toaster,
} from '@actovos-consulting-group/ui-core';
import { MdAddAPhoto } from '@react-icons/all-files/md/MdAddAPhoto';
import 'cropperjs/dist/cropper.css';
import firebase from 'firebase/app';
import Slider, { SliderTooltip } from 'rc-slider';
import 'rc-slider/assets/index.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Cropper } from 'react-cropper';
import { useDropzone } from 'react-dropzone';
import { useAsyncFn, useToggle } from 'react-use';
import styled, { css } from 'styled-components';

const Dropzone = styled(Block)(
  ({ theme, isDragActive, $isCircle }) => css`
    width: 200px;
    min-height: 200px;
    height: 200px;
    display: flex;
    background-color: ${theme.colors.reallyLightGrey};
    color: ${theme.colors.reallyLightGrey};
    justify-content: center;
    align-items: center;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    position: relative;

    ${$isCircle &&
    `
      border-radius: 50%;
    `}

    .add-photo {
      opacity: 0;
    }

    &:hover {
      .add-photo {
        opacity: 0.7;
      }
    }

    ${isDragActive &&
    css`
      border-color: ${theme.colors.primary};
    `}
  `,
);

const StyledCropper = styled(Cropper)`
  max-width: 600px;
  max-height: 600px;

  ${p =>
    p.$isCircle &&
    `
    .cropper-face {
      border-radius: 50%;
      border: 2px dashed black;
    }
  `}
`;

const Avatar = styled.img(
  ({ theme }) => css`
    width: 200px;
    height: 200px;
    border: 1px solid ${theme.colors.baseOffset};
  `,
);

const PhotoIcon = styled(MdAddAPhoto)``;

const Overlay = styled(Flex)`
  height: 100%;
  width: 100%;
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  align-items: center;
  justify-content: center;
`;

const AvatarUpload = ({
  storagePath,
  isCircle,
  onUpload,
  fileName,
  onDelete,
}) => {
  const [isReady, setReady] = useState(false);
  const [minZoom, setMin] = useState();
  const [url, setURL] = useState('');
  const [file, setFile] = useState({});
  const [show, toggleModal] = useToggle(false);
  const [cropper, setCropper] = useState();
  const path = `${storagePath}/${fileName}`;
  const imageData = cropper && cropper.getImageData();

  const [{ loading: processing }, postData] = useAsyncFn(
    async blob => {
      await firebase.storage().ref().child(path).put(blob);
      await onUpload(fileName);
    },
    [path],
  );

  const [{ loading: deleting }, deleteImage] = useAsyncFn(async () => {
    await firebase.storage().ref().child(path).delete();
    setURL('');
    await onDelete(fileName);
  });

  useEffect(() => {
    if (storagePath) {
      const ref = firebase.storage().ref(path);
      ref
        .getDownloadURL()
        .then(val => {
          setURL(val);
        })
        .catch(() => {
          //
        });
    }
  }, [processing, path]);

  const [{ loading }, submitImage] = useAsyncFn(async () => {
    await cropper.getCroppedCanvas().toBlob(async blob => {
      await postData(blob);
    });
    toggleModal();
  }, [cropper]);

  const onDropAccepted = useCallback(acceptedFiles => {
    const newFile = acceptedFiles[0];
    newFile.preview = URL.createObjectURL(newFile);

    setFile(newFile);
    toggleModal();
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: '.jpg,.jpeg,.png',
    onDropAccepted,
    onDropRejected: () => {
      Toaster.alert({
        value: 'You must upload a .jpeg, .jpg, or .png image.',
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    if (imageData) {
      const minSliderZoom = imageData.width / imageData.naturalWidth;
      setMin(minSliderZoom);
    }
  }, [imageData, cropper, file]);

  const onSlide = props => {
    const { value, dragging, index, ...restProps } = props;

    if (cropper && value && imageData && isReady && cropper.canvasData) {
      const zoomValue = value / 10;
      cropper.zoomTo(zoomValue.toFixed(4));
    }

    return (
      <SliderTooltip
        prefixCls="rc-slider-tooltip"
        overlay={`${value} %`}
        visible={dragging}
        placement="top"
        key={index}
      >
        <Slider.Handle
          value={value}
          {...restProps}
          style={{ height: 40, width: 40, marginTop: -12 }}
        />
      </SliderTooltip>
    );
  };

  return (
    <>
      <Flex flexDirection="column" alignItems="center">
        <Dropzone $isCircle={isCircle} {...getRootProps({ isDragActive })}>
          <input {...getInputProps()} />
          {url && <Avatar src={url} alt="profile-avatar" />}
          <Overlay
            bg={url ? 'black' : 'transparent'}
            className={url ? 'add-photo' : ''}
          >
            <PhotoIcon color={url ? 'white' : 'black'} size={25} />
          </Overlay>
        </Dropzone>
        {url && (
          <Button
            loading={deleting}
            style={{ marginTop: 10 }}
            size="sm"
            variant="danger"
            onClick={deleteImage}
          >
            Remove Photo
          </Button>
        )}
      </Flex>
      <Modal
        show={show}
        onClose={() => {
          toggleModal();
          setReady(false);
        }}
      >
        <ModalHeader>Crop Image</ModalHeader>
        {show && (
          <ModalContent>
            <StyledCropper
              $isCircle={isCircle}
              initialAspectRatio={1}
              guides
              src={file.preview}
              responsive
              cropBoxMovable={false}
              cropBoxResizable={false}
              toggleDragModeOnDblclick={false}
              minCropBoxHeight={200}
              minCropBoxWidth={200}
              autoCropArea={1}
              dragMode="move"
              zoomOnWheel={false}
              aspectRatio={1}
              ready={() => {
                setReady(true);
              }}
              checkOrientation={false}
              onInitialized={instance => {
                setCropper(instance);
              }}
            />
            <Block my={50} mx={50}>
              <Slider
                railStyle={{ height: 15 }}
                trackStyle={{ height: 15 }}
                min={minZoom * 10}
                max={10}
                defaultValue={0}
                handle={onSlide}
              />
            </Block>
          </ModalContent>
        )}
        <ModalFooter>
          <Flex justifyContent="flex-end">
            <Button loading={loading || processing} onClick={submitImage}>
              Save Image
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AvatarUpload;
